import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar, Clock, MapPin, DollarSign, Plus, AlertCircle, Repeat, Route, X } from 'lucide-react';
import { toast } from 'sonner';
import { PasajeroItem } from '../components/PasajeroItem';


export const SolicitarViajePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [tarifasPredef, setTarifasPredef] = useState([]);
  const [tiposViaje, setTiposViaje] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [tarifaManual, setTarifaManual] = useState(false);
  const [proximoFolio, setProximoFolio] = useState(null);
  
  // Lista de pasajeros con origen y destino
  const [pasajeros, setPasajeros] = useState([
    { nombre: '', telefono: '', correo: '', origen: '', destino: '' }
  ]);

  const [formData, setFormData] = useState({
    cliente_id: '',
    usuario_solicitante_id: user?.role === 'client' ? user?.id : '',
    fecha: '',
    hora: '',
    centro_costo: 'ninguno',
    tipo_viaje: '',
    tarifa: 0,
    observaciones: '',
    conductor_id: '',
    vehiculo_id: ''
  });
  const [desvios, setDesvios] = useState([]); // array de strings
  const [tieneRetorno, setTieneRetorno] = useState(false);

  // Bloquear tarifa automática si hay más de un pasajero
  const tarifaAutomaticaBloqueada = pasajeros.length > 1;

  // Cargar próximo folio al inicio
  useEffect(() => {
    const loadProximoFolio = async () => {
      try {
        const response = await api.get('/servicios/proximo-folio');
        setProximoFolio(response.data.proximo_folio);
      } catch (error) {
        console.log('Error cargando próximo folio');
      }
    };
    loadProximoFolio();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      loadClientes();
    } else if (user?.role === 'client' && user?.cliente_id) {
      // Para clientes, cargar centros de costo de su cliente
      loadCentrosCostoCliente(user.cliente_id);
    }
    loadTarifas();
    loadTiposViaje();
    if (user?.role === 'admin') {
      loadConductores();
      loadVehiculos();
    }
  }, [user]);

  useEffect(() => {
    if (formData.cliente_id && user?.role === 'admin') {
      loadUsuariosByCliente(formData.cliente_id);
      loadCentrosCostoCliente(formData.cliente_id);
    }
  }, [formData.cliente_id, user]);

  // Calcular tarifa solo si hay un pasajero y tiene origen/destino
  useEffect(() => {
    if (pasajeros.length === 1 && pasajeros[0].origen && pasajeros[0].destino && !tarifaManual) {
      calcularTarifa(pasajeros[0].origen, pasajeros[0].destino);
    }
  }, [pasajeros, tarifaManual]);

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const loadConductores = async () => {
    try {
      const response = await api.get('/conductores');
      setConductores(response.data || []);
    } catch (error) {
      console.error('Error al cargar conductores');
    }
  };

  const loadVehiculos = async () => {
    try {
      const response = await api.get('/vehiculos');
      setVehiculos(response.data || []);
    } catch (error) {
      console.error('Error al cargar vehículos');
    }
  };

  const loadUsuariosByCliente = async (clienteId) => {
    try {
      const response = await api.get(`/clientes/${clienteId}/usuarios`);
      setUsuarios(response.data);
    } catch (error) {
      console.error('Error al cargar usuarios');
      setUsuarios([]);
    }
  };

  const loadCentrosCostoCliente = async (clienteId) => {
    try {
      const response = await api.get(`/clientes/${clienteId}/centros-costo`);
      if (response.data && response.data.centros_costo) {
        setCentrosCosto(response.data.centros_costo);
      } else {
        setCentrosCosto([]);
      }
    } catch (error) {
      console.error('Error al cargar centros de costo');
      setCentrosCosto([]);
    }
  };

  const loadTarifas = async () => {
    try {
      const response = await api.get('/tarifas');
      setTarifasPredef(response.data);
    } catch (error) {
      console.error('Error al cargar tarifas');
    }
  };

  const loadTiposViaje = async () => {
    try {
      const response = await api.get('/tipos-viaje');
      setTiposViaje(response.data || []);
    } catch (error) {
      console.error('Error al cargar tipos de viaje');
    }
  };

  const calcularTarifa = async (origen, destino) => {
    try {
      const response = await api.get('/tarifas/calcular', {
        params: { origen, destino }
      });
      setFormData(prev => ({ ...prev, tarifa: response.data.tarifa }));
    } catch (error) {
      console.error('Error al calcular tarifa');
    }
  };

  // Funciones para manejar pasajeros
  const agregarPasajero = () => {
    setPasajeros([...pasajeros, { nombre: '', telefono: '', correo: '', origen: '', destino: '' }]);
    // Al agregar más pasajeros, resetear tarifa si no es manual
    if (!tarifaManual) {
      setFormData(prev => ({ ...prev, tarifa: 0 }));
    }
  };

  const actualizarPasajero = (index, data) => {
    const nuevosPasajeros = [...pasajeros];
    nuevosPasajeros[index] = data;
    setPasajeros(nuevosPasajeros);
  };

  const eliminarPasajero = (index) => {
    if (pasajeros.length > 1) {
      const nuevosPasajeros = pasajeros.filter((_, i) => i !== index);
      setPasajeros(nuevosPasajeros);
    }
  };

  const moverPasajeroArriba = (index) => {
    if (index > 0) {
      const nuevosPasajeros = [...pasajeros];
      [nuevosPasajeros[index - 1], nuevosPasajeros[index]] = [nuevosPasajeros[index], nuevosPasajeros[index - 1]];
      setPasajeros(nuevosPasajeros);
    }
  };

  const moverPasajeroAbajo = (index) => {
    if (index < pasajeros.length - 1) {
      const nuevosPasajeros = [...pasajeros];
      [nuevosPasajeros[index], nuevosPasajeros[index + 1]] = [nuevosPasajeros[index + 1], nuevosPasajeros[index]];
      setPasajeros(nuevosPasajeros);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Formatear pasajeros para el backend (sin validaciones bloqueantes)
      const pasajerosStr = pasajeros.map(p => p.nombre || 'Sin nombre').join(', ');
      const primerPasajero = pasajeros[0];
      const ultimoPasajero = pasajeros[pasajeros.length - 1];

      // Construir detalle de ruta
      const rutaDetalle = pasajeros.map((p, i) => 
        `${i + 1}. ${p.nombre || 'Sin nombre'}: ${p.origen || '-'} → ${p.destino || '-'}`
      ).join('\n');

      // Obtener empresa_id del cliente seleccionado o del usuario
      const clienteSeleccionado = clientes.find(c => c.id === formData.cliente_id);
      const empresaId = clienteSeleccionado?.empresa_id || user?.empresa_id;

      const servicioData = {
        ...formData,
        empresa_id: empresaId,
        usuario_solicitante_id: formData.usuario_solicitante_id || user?.id,
        pasajeros: pasajerosStr,
        nombre_pasajero: primerPasajero.nombre || 'Sin nombre',
        telefono: primerPasajero.telefono || '',
        correo: primerPasajero.correo || '',
        origen: primerPasajero.origen || '',
        destino: pasajeros.length === 1 ? (primerPasajero.destino || '') : (ultimoPasajero.destino || ''),
        centro_costo: formData.centro_costo === 'ninguno' ? '' : formData.centro_costo,
        tarifa_manual: tarifaManual || pasajeros.length > 1,
        creado_por: user?.role,
        conductor_id: formData.conductor_id || null,
        vehiculo_id: formData.vehiculo_id || null,
        // Guardar detalle completo en observaciones cuando hay múltiples pasajeros
        observaciones: pasajeros.length > 1 
          ? `${formData.observaciones || ''}\n\n--- Detalle de Ruta (orden de recogida) ---\n${rutaDetalle}\n\n--- Contactos ---\n${pasajeros.map((p, i) => {
              const partes = [`${i + 1}. ${p.nombre || 'Sin nombre'}`];
              if (p.telefono) partes.push(`Tel: ${p.telefono}`);
              if (p.correo) partes.push(`Email: ${p.correo}`);
              return partes.join(' | ');
            }).join('\n')}`.trim()
          : formData.observaciones,
        desvios: desvios.map(d => d.trim()).filter(Boolean),
        tiene_retorno: tieneRetorno,
      };

      // Eliminar cliente_id del objeto antes de enviar (no es campo del servicio)
      delete servicioData.cliente_id;

      await api.post('/servicios', servicioData);
      toast.success('Servicio solicitado exitosamente');
      
      if (user?.role === 'admin') {
        navigate('/admin/calendario');
      } else {
        navigate('/cliente/calendario');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al solicitar servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const origenes = [...new Set(tarifasPredef.map(t => t.origen))];
  const destinos = [...new Set(tarifasPredef.map(t => t.destino))];

  return (
    <div className="max-w-4xl mx-auto" data-testid="solicitar-viaje-page">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Solicitar Viaje</h1>
            <p className="text-gray-600 mt-1">Complete el formulario para solicitar un nuevo traslado</p>
          </div>
          {proximoFolio && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2 rounded-lg text-center">
              <span className="text-xs font-medium block">Folio Provisorio</span>
              <span className="text-2xl font-bold">#{proximoFolio}</span>
            </div>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información del Servicio</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {user?.role === 'admin' && (
              <>
                <div>
                  <Label htmlFor="cliente">Cliente</Label>
                  <Select
                    value={formData.cliente_id}
                    onValueChange={(value) => handleChange('cliente_id', value)}
                  >
                    <SelectTrigger id="cliente" data-testid="cliente-select">
                      <SelectValue placeholder="Seleccionar cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientes.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="usuario">Usuario Solicitante (opcional)</Label>
                  <Select
                    value={formData.usuario_solicitante_id}
                    onValueChange={(value) => handleChange('usuario_solicitante_id', value)}
                  >
                    <SelectTrigger id="usuario" data-testid="usuario-select">
                      <SelectValue placeholder="Seleccionar usuario (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {usuarios.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.nombre} - {u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fecha">Fecha</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="fecha"
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => handleChange('fecha', e.target.value)}
                    className="pl-10"
                    data-testid="fecha-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="hora">Hora</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="hora"
                    type="time"
                    value={formData.hora}
                    onChange={(e) => handleChange('hora', e.target.value)}
                    className="pl-10"
                    data-testid="hora-input"
                  />
                </div>
              </div>
            </div>

            {user?.role === 'admin' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="asignacion-recursos-inline">
                <div>
                  <Label htmlFor="conductor">Conductor (opcional)</Label>
                  <Select
                    value={formData.conductor_id || '__none__'}
                    onValueChange={(v) => handleChange('conductor_id', v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger id="conductor" data-testid="conductor-select">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      {conductores.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre}{c.rut ? ` · ${c.rut}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="vehiculo">Vehículo (opcional)</Label>
                  <Select
                    value={formData.vehiculo_id || '__none__'}
                    onValueChange={(v) => handleChange('vehiculo_id', v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger id="vehiculo" data-testid="vehiculo-select">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin asignar</SelectItem>
                      {vehiculos.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.patente || v.placa} · {v.modelo || v.marca}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {tiposViaje.length > 0 && (
              <div>
                <Label htmlFor="tipo_viaje">Tipo de Viaje</Label>
                <Select
                  value={formData.tipo_viaje || "ninguno"}
                  onValueChange={(value) => handleChange('tipo_viaje', value === "ninguno" ? "" : value)}
                >
                  <SelectTrigger id="tipo_viaje" data-testid="tipo-viaje-select">
                    <SelectValue placeholder="Seleccionar tipo de viaje" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Sin especificar</SelectItem>
                    {tiposViaje.map(t => (
                      <SelectItem key={t.id} value={t.nombre}>{t.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sección de Pasajeros */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Pasajeros ({pasajeros.length})</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={agregarPasajero}
                  className="gap-1"
                  data-testid="agregar-pasajero-btn"
                >
                  <Plus size={16} />
                  Agregar Pasajero
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                El orden de los pasajeros indica el orden de recogida. Cada pasajero tiene su propio origen y destino.
              </p>
              
              <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                {pasajeros.map((pasajero, index) => (
                  <PasajeroItem
                    key={index}
                    pasajero={pasajero}
                    index={index}
                    total={pasajeros.length}
                    onUpdate={actualizarPasajero}
                    onRemove={eliminarPasajero}
                    onMoveUp={moverPasajeroArriba}
                    onMoveDown={moverPasajeroAbajo}
                    origenes={origenes}
                    destinos={destinos}
                  />
                ))}
              </div>
            </div>

            {centrosCosto.length > 0 && (
              <div>
                <Label htmlFor="centro_costo">Centro de Costo (Opcional)</Label>
                <Select
                  value={formData.centro_costo || "ninguno"}
                  onValueChange={(value) => handleChange('centro_costo', value === "ninguno" ? "" : value)}
                >
                  <SelectTrigger id="centro_costo" data-testid="centro-costo-select">
                    <SelectValue placeholder="Seleccionar centro de costo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Ninguno</SelectItem>
                    {centrosCosto.map(cc => (
                      <SelectItem key={cc} value={cc}>{cc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sección de Tarifa - Solo visible para administradores */}
            {user?.role === 'admin' && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
                {/* Alerta cuando hay múltiples pasajeros */}
                {tarifaAutomaticaBloqueada && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                    <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Tarifa manual requerida</p>
                      <p className="text-xs mt-0.5">Con múltiples pasajeros y rutas, debe ingresar la tarifa manualmente.</p>
                    </div>
                  </div>
                )}

                {!tarifaAutomaticaBloqueada && (
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="tarifa-manual" 
                      checked={tarifaManual}
                      onCheckedChange={setTarifaManual}
                      data-testid="tarifa-manual-checkbox"
                    />
                    <Label htmlFor="tarifa-manual" className="text-sm font-medium cursor-pointer">
                      Ingresar tarifa manualmente
                    </Label>
                  </div>
                )}

                <div>
                  <Label htmlFor="tarifa" className="text-sm">Tarifa (CLP) {tarifaAutomaticaBloqueada && '*'}</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary" size={20} />
                    <Input
                      id="tarifa"
                      type="number"
                      value={formData.tarifa === 0 && (tarifaManual || tarifaAutomaticaBloqueada) ? '' : formData.tarifa}
                      onChange={(e) => handleChange('tarifa', parseFloat(e.target.value) || 0)}
                      onWheel={(e) => e.target.blur()}
                      className="pl-10 text-lg font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      disabled={!tarifaManual && !tarifaAutomaticaBloqueada}
                      required={false}
                      placeholder={tarifaManual || tarifaAutomaticaBloqueada ? "Ingrese tarifa" : ""}
                      data-testid="tarifa-input"
                    />
                  </div>
                  {!tarifaAutomaticaBloqueada && !tarifaManual && (
                    <p className="text-xs text-gray-500 mt-1">
                      La tarifa se calcula automáticamente según origen y destino
                    </p>
                  )}
                </div>

                {formData.tarifa > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-sm text-gray-600">Total a pagar:</span>
                    <span className="text-2xl font-bold text-primary">
                      ${formData.tarifa.toLocaleString('es-CL')}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Ruta del vehículo: desvíos y retorno */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3" data-testid="ruta-vehiculo-section">
              <div className="flex items-center gap-2">
                <Route size={18} className="text-primary" />
                <h3 className="font-semibold text-gray-900">Ruta del Vehículo (opcional)</h3>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Agrega paradas intermedias (desvíos) o marca el viaje como ida y vuelta.
              </p>

              {/* Lista de desvíos */}
              {desvios.length > 0 && (
                <div className="space-y-2">
                  {desvios.map((d, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`desvio-row-${idx}`}>
                      <span className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <Input
                        value={d}
                        onChange={(e) => {
                          const next = [...desvios];
                          next[idx] = e.target.value;
                          setDesvios(next);
                        }}
                        placeholder={`Desvío ${idx + 1}: dirección de la parada intermedia`}
                        data-testid={`desvio-input-${idx}`}
                        className="bg-white"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setDesvios(desvios.filter((_, i) => i !== idx))}
                        title="Quitar desvío"
                        data-testid={`desvio-remove-${idx}`}
                      >
                        <X size={16} className="text-red-600" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setDesvios([...desvios, ''])}
                  data-testid="add-desvio-button"
                >
                  <Plus size={14} className="mr-1" /> Agregar Desvío
                </Button>
                <Button
                  type="button"
                  variant={tieneRetorno ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTieneRetorno(!tieneRetorno)}
                  data-testid="toggle-retorno-button"
                  className={tieneRetorno ? '' : ''}
                >
                  <Repeat size={14} className="mr-1" />
                  {tieneRetorno ? 'Quitar Retorno' : 'Agregar Retorno (ida y vuelta)'}
                </Button>
              </div>

              {tieneRetorno && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                  🔄 El vehículo regresará al origen tras dejar a los pasajeros.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="observaciones">Observaciones (Opcional)</Label>
              <Input
                id="observaciones"
                value={formData.observaciones || ''}
                onChange={(e) => handleChange('observaciones', e.target.value)}
                placeholder="Observaciones adicionales del viaje"
                data-testid="observaciones-input"
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
                data-testid="submit-button"
              >
                {loading ? 'Solicitando...' : 'Solicitar Viaje'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
